import React from "react";
import {
  clearRowSeats,
  generateSeats,
  hasSeatAt,
  normalizeRowLabel,
  renumberSeats,
  rowIndexToLabel,
  rowLabelToIndex,
  selectRowSeats,
  toggleSeatAt,
} from "../lib/venueLayout";

const SeatLayoutBuilder = ({
  rowCount,
  columnCount,
  startRowLabel = "A",
  startColumnNumber = 1,
  numberDirection = "ltr",
  seats = [],
  onRowCountChange,
  onColumnCountChange,
  onStartRowLabelChange,
  onStartColumnNumberChange,
  onNumberDirectionChange,
  onSeatsChange,
  onGenerate,
}) => {
  const rows = Math.max(0, Number(rowCount) || 0);
  const cols = Math.max(0, Number(columnCount) || 0);
  const seatSize = rows >= 15 || cols >= 20 ? 22 : rows >= 10 || cols >= 14 ? 26 : 30;

  const startRowIndex = rowLabelToIndex(startRowLabel) ?? 0;
  const startColumn = Math.max(1, parseInt(startColumnNumber, 10) || 1);
  const origin = {
    startRowIndex,
    startColumnNumber: startColumn,
    numberDirection: numberDirection === "rtl" ? "rtl" : "ltr",
  };
  const labelsByPosition = new Map(
    renumberSeats(seats, origin).map((seat) => [
      `${seat.row_position}:${seat.column_position}`,
      seat.seat_label,
    ])
  );

  return (
    <div className="space-y-4 min-w-0">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs font-bold uppercase text-gray-500">
          Rows
          <input
            type="number"
            min="1"
            max="40"
            value={rowCount || ""}
            onChange={(event) => onRowCountChange(event.target.value)}
            className="mt-1 block w-24 rounded-lg border border-gray-200 p-2 text-sm text-gray-900"
          />
        </label>
        <label className="text-xs font-bold uppercase text-gray-500">
          Columns
          <input
            type="number"
            min="1"
            max="40"
            value={columnCount || ""}
            onChange={(event) => onColumnCountChange(event.target.value)}
            className="mt-1 block w-24 rounded-lg border border-gray-200 p-2 text-sm text-gray-900"
          />
        </label>
        <label className="text-xs font-bold uppercase text-gray-500">
          Starting Row
          <input
            type="text"
            inputMode="text"
            value={startRowLabel || ""}
            onChange={(event) => onStartRowLabelChange(normalizeRowLabel(event.target.value))}
            placeholder="A"
            className="mt-1 block w-24 rounded-lg border border-gray-200 p-2 text-sm uppercase text-gray-900"
          />
        </label>
        <label className="text-xs font-bold uppercase text-gray-500">
          Starting Column
          <input
            type="number"
            min="1"
            step="1"
            value={startColumnNumber ?? ""}
            onChange={(event) => onStartColumnNumberChange(event.target.value)}
            placeholder="1"
            className="mt-1 block w-28 rounded-lg border border-gray-200 p-2 text-sm text-gray-900"
          />
        </label>
        <label className="text-xs font-bold uppercase text-gray-500">
          Seat 1 starts
          <select
            value={origin.numberDirection}
            onChange={(event) => onNumberDirectionChange?.(event.target.value)}
            className="mt-1 block w-40 rounded-lg border border-gray-200 bg-white p-2 text-sm text-gray-900"
          >
            <option value="ltr">Left to right</option>
            <option value="rtl">Right to left</option>
          </select>
        </label>
        <button
          type="button"
          onClick={onGenerate}
          className="rounded-lg bg-brand-maroon px-4 py-2 text-xs font-bold uppercase text-white"
        >
          Generate grid
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onSeatsChange(generateSeats(rows, cols, origin))}
          disabled={!rows || !cols}
          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-bold uppercase text-gray-700 disabled:opacity-40"
        >
          Select All
        </button>
        <button
          type="button"
          onClick={() => onSeatsChange([])}
          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-bold uppercase text-gray-700"
        >
          Clear All
        </button>
      </div>

      {rows > 0 && cols > 0 ? (
        <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-slate-50 p-4">
          <div className="min-w-max">
            <div className="mb-4 flex justify-center">
              <div className="w-40 rounded-full bg-slate-900 py-2 text-center text-[10px] uppercase tracking-[0.3em] text-white">
                Stage
              </div>
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <div className="flex items-center gap-2">
                <div className="w-8" />
                <div className="flex items-center gap-1">
                  {Array.from({ length: cols }, (_, colIndex) => (
                    <div
                      key={`col-${colIndex + 1}`}
                      className="text-center text-[9px] font-bold text-slate-500"
                      style={{ width: `${seatSize}px` }}
                    >
                      {colIndex + 1}
                    </div>
                  ))}
                </div>
                <div className="w-[74px]" />
              </div>

              {Array.from({ length: rows }, (_, rowIndex) => {
                const rowPosition = rowIndex + 1;
                const rowLabel = rowIndexToLabel(startRowIndex + rowIndex);
                return (
                  <div key={rowLabel} className="flex items-center gap-2">
                    <div className="w-8 text-right text-xs font-bold text-slate-700">{rowLabel}</div>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: cols }, (_, colIndex) => {
                        const columnPosition = colIndex + 1;
                        const isSeat = hasSeatAt(seats, rowPosition, columnPosition);
                        const label = labelsByPosition.get(`${rowPosition}:${columnPosition}`) || "";
                        return (
                          <button
                            key={`${rowPosition}:${columnPosition}`}
                            type="button"
                            title={
                              isSeat
                                ? `${label} (click to make empty)`
                                : "Empty cell (click to add a seat)"
                            }
                            onClick={() =>
                              onSeatsChange(toggleSeatAt(seats, rowPosition, columnPosition, origin))
                            }
                            className={`flex items-center justify-center rounded border text-[9px] font-bold ${
                              isSeat
                                ? "border-emerald-500 bg-emerald-300 text-slate-800"
                                : "border-dashed border-slate-300 bg-transparent text-slate-400"
                            }`}
                            style={{ width: `${seatSize}px`, height: `${seatSize}px` }}
                          >
                            {isSeat ? label : ""}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => onSeatsChange(selectRowSeats(seats, rowPosition, cols, origin))}
                        className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[9px] font-bold uppercase text-gray-600"
                      >
                        Row
                      </button>
                      <button
                        type="button"
                        onClick={() => onSeatsChange(clearRowSeats(seats, rowPosition))}
                        className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[9px] font-bold uppercase text-gray-600"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-4 text-center text-[11px] text-slate-500">
              {seats.length} physical seat{seats.length === 1 ? "" : "s"}. Empty cells are not stored.
            </p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">Enter rows and columns, then generate a grid.</p>
      )}
    </div>
  );
};

export default SeatLayoutBuilder;
